import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from "flarum/forum/app";
import { purgeSnapshots, readSnapshot, serialize, writeSnapshot } from "../utils/snapshot";

/**
 * One channel's loaded message window plus its paging state.
 */

const PAGE_SIZE = 50;

/** How many recently viewed channels the local snapshot keeps a tail for. */
const SNAPSHOT_CHANNELS = 3;

/** Quiet period before a snapshot write; see scheduleSnapshot(). */
const SNAPSHOT_DEBOUNCE_MS = 1500;

/**
 * The collection size a paginated response reports, or null when it does not.
 *
 * `Store#find` hangs the raw document off the array it returns, which is the
 * only way to reach anything outside `data` — and json-api-server puts the count
 * of the whole collection in `meta.page.total`. Reading it turns a `limit: 1`
 * request into "the newest one, and how many there are", for the price of the
 * one it was already making.
 */
function readTotal(results) {
  var _payload;
  const total = results == null || (_payload = results.payload) == null || (_payload = _payload.meta) == null || (_payload = _payload.page) == null ? void 0 : _payload.total;
  return typeof total === "number" ? total : null;
}

/**
 * Single source of truth for the chat UI.
 *
 * Components read from here and stay presentational. Keeping paging, read state,
 * optimistic sends and realtime reconciliation in one place is what stops the
 * drawer and the full-screen page from drifting apart — they are two views over
 * this one object.
 */
export default class ChatState {
  constructor() {
    /** Channels in the sidebar, newest activity first. */
    _defineProperty(this, "channels", []);
    _defineProperty(this, "channelsLoading", false);
    _defineProperty(this, "channelsLoaded", false);
    /**
     * The in-flight channel request, so concurrent callers share one round trip.
     *
     * The old guard returned `this.channels` the moment a request was already
     * running, which is not the same thing: on a cold start that array is still
     * empty, so whichever caller arrived second was handed `[]` and carried on as
     * though the chat had no channels. Mount and the full-screen page's boot both
     * fire on a direct visit to /chat, so the two raced on every such visit.
     */
    _defineProperty(this, "channelsRequest", null);
    /**
     * Whether drafts have been fetched this session.
     *
     * Drafts are written back by the composer as they change, so once fetched the
     * in-memory copy is the newer of the two — refetching would at best confirm
     * what is already held and at worst overwrite it with a slower round trip.
     */
    _defineProperty(this, "draftsLoaded", false);
    _defineProperty(this, "draftsRequest", null);
    /** The sidebar came from the local snapshot; refetch it on first use. */
    _defineProperty(this, "channelsStale", false);
    /** Channels whose pinned preview came from the local snapshot. */
    _defineProperty(this, "pinnedStale", new Set());
    /** Pending debounced snapshot write, if any. */
    _defineProperty(this, "snapshotTimer", null);
    /**
     * Channels viewed this session, most recent first. What the snapshot keeps
     * message tails for — the ones the reader is likely to open next visit.
     */
    _defineProperty(this, "recentChannelIds", []);
    /** Loaded message windows, keyed by channel id. */
    _defineProperty(this, "streams", {});
    /** Threads loaded per channel, keyed by channel id. */
    _defineProperty(this, "threads", {});
    /** Loaded message windows for opened threads, keyed by thread id. */
    _defineProperty(this, "threadStreams", {});
    /**
     * The newest pinned message per channel, for the bar above the stream.
     *
     * Fetched separately because the pinned message is usually far above the loaded
     * window — that is the point of pinning it. `null` means "asked, nothing pinned",
     * which is distinct from `undefined`, "not asked yet".
     */
    _defineProperty(this, "pinnedPreviews", {});
    /**
     * How many pinned messages each channel has, as the server last counted them.
     *
     * Read from the preview request's own pagination meta rather than asked for
     * separately. Kept apart from `pinnedPreviews` because it answers a different
     * question — that one is "what does the bar show", this one is "is there more
     * than that" — and because a channel can have a total without a preview when
     * the preview request failed.
     */
    _defineProperty(this, "pinnedTotals", {});
    /** Composer drafts, keyed by `channelId` or `channelId:threadId`. */
    _defineProperty(this, "drafts", {});
    /** Typing indicators, keyed by channel id then user id. */
    _defineProperty(this, "typing", {});
    /** Currently viewed channel and thread. */
    _defineProperty(this, "activeChannelId", null);
    _defineProperty(this, "activeThreadId", null);
    /**
     * Whether the pinned-messages panel is open.
     *
     * It shares the right-hand slot with the thread panel, so opening one closes the
     * other — see togglePinned() / setActiveThread().
     */
    _defineProperty(this, "showPinned", false);
    /**
     * Whether the drawer is showing search instead of the conversation.
     *
     * Drawer-only, and it exists because the full-screen page has a route for this
     * and the drawer has none: `chat.search` is a page, so reaching it from the
     * drawer navigated away and closed the drawer to do it — searching a
     * conversation threw you out of the window you were searching from.
     *
     * Shares the overlay slot with the thread and pinned panels; the three toggles
     * clear each other.
     */
    _defineProperty(this, "showSearch", false);
    /**
     * Drawer open/collapsed state.
     *
     * Persisted, so the drawer survives a reload and closes only when the user
     * explicitly dismisses it. Mutate through the setters below rather than
     * assigning directly, or the change will not be remembered.
     */
    _defineProperty(this, "drawerOpen", false);
    _defineProperty(this, "drawerCollapsed", false);
    /**
     * The drawer was closed to go full screen, not dismissed.
     *
     * Both leave `drawerOpen` false, and the difference matters on the way back: a
     * drawer the user closed with the X should stay closed, while one that only
     * stepped aside for the full-screen page should come back when the page is left.
     * Without this the two were indistinguishable, so returning to the forum always
     * looked like the chat had been closed.
     */
    _defineProperty(this, "drawerSuspended", false);
    /** Selection mode, for quote/copy/move. */
    _defineProperty(this, "selecting", false);
    _defineProperty(this, "selected", new Set());
    /**
     * Message being replied to or edited, keyed by composer scope — the same
     * `channelId` / `channelId:threadId` key the drafts use.
     *
     * Scoped rather than global because the channel and an open thread render two
     * composers over this one state object. Sharing a single `editing` field meant
     * starting an edit in the thread panel put the channel composer into edit mode
     * too, and sending from there would PATCH that message from the wrong scope.
     */
    _defineProperty(this, "replyTargets", {});
    _defineProperty(this, "editTargets", {});
    /**
     * Reply scopes whose next send should branch into a new thread.
     *
     * Replying and branching stage the same way — both put a target in
     * `replyTargets` — so without recording which one was asked for, the composer
     * had nothing to tell them apart and inferred it: any reply in a channel with
     * threading on became a thread, and a plain reply was unreachable.
     */
    _defineProperty(this, "branchTargets", {});
    /** Attachments staged in the composer but not yet sent. */
    _defineProperty(this, "pendingUploads", []);
    /**
     * Optimistic rows awaiting their server response, keyed by a client-generated
     * token. They are rendered greyed out and replaced in place on success.
     */
    _defineProperty(this, "pending", new Map());
    _defineProperty(this, "pendingSeq", 0);
    // ───────────────────────────────────────────────────────────────────────────
    // Boot payload
    // ───────────────────────────────────────────────────────────────────────────
    /** Whether the boot payload has already been read; it is only ever good once. */
    _defineProperty(this, "bootHydrated", false);
    /**
     * Who wants to know when a channel's membership changes.
     *
     * The members tab is the one surface drawn from a list the realtime payload
     * cannot carry, so it re-reads the list when told. Listeners rather than a
     * counter on the state: a modal that is not open should cost nothing.
     */
    _defineProperty(this, "membershipListeners", new Set());
    /**
     * The channel's thread list, in the drawer's overlay slot.
     *
     * Drawer-only for the same reason search is: the page has `chat.threads`
     * as a route, and routing from the drawer closes it.
     */
    _defineProperty(this, "showThreads", false);
    _defineProperty(this, "draftTimer", null);
    _defineProperty(this, "typingSentAt", 0);
  }
  // ───────────────────────────────────────────────────────────────────────────
  // Drawer persistence
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Keyed by user so a shared browser cannot restore one account's open channel
   * into another's session.
   */
  storageKey() {
    var _app$session$user;
    const id = (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id();
    return id ? "ramon-chat.drawer.".concat(id) : null;
  }

  /**
   * Reads back the drawer state saved by a previous visit.
   *
   * Returns whether the drawer should be open, so the caller can decide whether
   * to pay for loading the channel list.
   */
  restoreDrawer() {
    const key = this.storageKey();
    if (!key) return false;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      const saved = JSON.parse(raw);
      this.drawerOpen = Boolean(saved.open);
      this.drawerCollapsed = Boolean(saved.collapsed);
      // Persisted so a reload while on the full-screen page does not lose the fact
      // that the drawer is owed a reopening.
      this.drawerSuspended = Boolean(saved.suspended);
      if (saved.channelId) {
        this.activeChannelId = Number(saved.channelId);
      }
      return this.drawerOpen;
    } catch (_unused) {
      // Private browsing, a quota error, or hand-edited garbage. Losing the
      // restored position is not worth breaking startup over.
      return false;
    }
  }
  persistDrawer() {
    this.persistDrawerCookie();
    const key = this.storageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({
        open: this.drawerOpen,
        collapsed: this.drawerCollapsed,
        suspended: this.drawerSuspended,
        channelId: this.activeChannelId
      }));
    } catch (_unused2) {
      // Non-fatal, as above.
    }
  }

  /**
   * Mirrors "drawer open, on this channel" into a cookie the server can read.
   *
   * localStorage is invisible to the page render, so a reload on a forum page
   * with the drawer open used to boot with nothing preloaded and assemble the
   * conversation over three round trips. Content\PreloadChat reads this cookie
   * and puts the drawer's channel list and conversation into the boot payload
   * of every page, the way it already does for /chat routes.
   *
   * Only a channel id, never content; absent whenever the drawer is closed, so
   * the server does no chat work for a page the reader is not chatting on.
   */
  persistDrawerCookie() {
    try {
      const secure = location.protocol === "https:" ? "; Secure" : "";
      if (this.drawerOpen && !this.drawerSuspended) {
        var _this$activeChannelId;
        document.cookie = "ramon_chat_drawer=".concat((_this$activeChannelId = this.activeChannelId) != null ? _this$activeChannelId : 0, "; path=/; max-age=31536000; SameSite=Lax").concat(secure);
      } else {
        document.cookie = "ramon_chat_drawer=; path=/; max-age=0; SameSite=Lax".concat(secure);
      }
    } catch (_unused3) {
      // Non-fatal.
    }
  }
  setDrawerOpen(open) {
    this.drawerOpen = open;

    // Opening or closing the drawer directly settles the question either way, so a
    // pending suspension is stale from here on. In particular this is what makes
    // the X final: closing by hand while suspended must not be undone later.
    this.drawerSuspended = false;
    this.persistDrawer();
  }

  /**
   * Closes the drawer because the full-screen page is taking over the view.
   *
   * Distinct from both `setDrawerOpen(false)`, which would clear a pending
   * suspension, and `suspendDrawer()`, which would create one: arriving at the page
   * by any route other than the drawer's own full-screen button must not earn a
   * reopening on the way out.
   */
  hideDrawerForPage() {
    this.drawerOpen = false;
    this.persistDrawer();
  }

  /**
   * Closes the drawer on the understanding that it is owed a reopening.
   *
   * Used when handing the conversation over to the full-screen page, which is the
   * one case where the drawer disappears without the user having dismissed it.
   */
  suspendDrawer() {
    this.drawerOpen = false;
    this.drawerSuspended = true;
    this.persistDrawer();
  }

  /**
   * Reopens a suspended drawer, and reports whether there was one.
   *
   * Consuming the flag here rather than at the call site keeps "restored at most
   * once" a property of the state instead of a rule each caller has to remember.
   */
  resumeDrawer() {
    if (!this.drawerSuspended) return false;
    this.drawerSuspended = false;
    this.drawerOpen = true;
    this.persistDrawer();
    return true;
  }
  setDrawerCollapsed(collapsed) {
    this.drawerCollapsed = collapsed;
    this.persistDrawer();
  }

  /**
   * Records which channel is being viewed, so a reload reopens the same one.
   */
  setActiveChannel(channelId) {
    this.activeChannelId = channelId;
    if (channelId !== null) {
      this.recentChannelIds = [channelId, ...this.recentChannelIds.filter(id => id !== channelId)].slice(0, SNAPSHOT_CHANNELS);
    }
    this.persistDrawer();
  }
  /**
   * Fills the state from the payload Content\PreloadChat put in the page.
   *
   * This is what removes the staggered flash the chat opened with. The list, the
   * conversation, the pinned strip and the drafts were four requests fired after
   * the page had already mounted, each swapping a placeholder for content as it
   * landed; here they are already answered, and reading them is synchronous, so
   * the first frame the user sees is the finished chat.
   *
   * Called from the initializer, before anything mounts. `app.data` is populated
   * by `Application#load()`, which runs ahead of the initializers, so the payload
   * is there — but `app.forum` and `app.session` are not yet, which is why
   * nothing here may touch them.
   *
   * Every step is optional and independently guarded. A section the server could
   * not produce, or a stream something has already loaded, is left alone and the
   * ordinary async path serves it.
   */
  hydrateFromBoot() {
    var _data;
    if (this.bootHydrated) return;
    this.bootHydrated = true;
    const boot = (_data = app.data) == null ? void 0 : _data.ramonChat;
    if (!boot) return;
    try {
      var _boot$channelId, _boot$threadId;
      this.hydrateChannels(boot.channels);
      this.hydrateDrafts(boot.drafts);
      const channelId = Number((_boot$channelId = boot.channelId) != null ? _boot$channelId : 0);
      if (channelId > 0) {
        this.hydrateStream(this.stream(channelId), boot.messages, channelId);
        this.hydratePinned(channelId, boot.pinned);
      }
      const threadId = Number((_boot$threadId = boot.threadId) != null ? _boot$threadId : 0);
      if (threadId > 0) {
        this.hydrateStream(this.threadStream(threadId), boot.threadMessages);
      }
    } catch (_unused4) {
      // A preload that broke the chat would be worse than the flash it removes.
      // Whatever was not hydrated is simply fetched the way it always was.
    }
  }

  /**
   * Seeds whatever the boot payload did not carry from the copy the last visit
   * left in localStorage — the sidebar and the tails of the channels most
   * recently viewed.
   *
   * Runs right after hydrateFromBoot() and before anything mounts, which is why
   * the snapshot lives in localStorage rather than IndexedDB: the read has to be
   * synchronous to land before the first view(), and at the size kept here
   * (see MAX_BYTES in utils/snapshot) it costs a few milliseconds. This is what
   * lets the chat open finished on a page the server did not preload — the
   * drawer opened by its button after a reload, or a channel visited last time.
   *
   * Nothing read here is trusted past the first paint. Each section is marked
   * stale, and the first thing that shows it replaces it with the server's
   * answer in the background — see loadChannels(), loadChannel() and
   * loadPinnedPreview(). The reader sees the conversation as it was, then the
   * few rows that changed, rather than a skeleton and then everything.
   *
   * Reads the user id from `app.data`, not `app.session`, for the same reason
   * hydrateFromBoot() does: the session is not built yet.
   */
  hydrateFromSnapshot() {
    const userId = this.snapshotUserId();
    if (!userId) return;
    const snapshot = readSnapshot(userId);
    if (!snapshot) return;
    try {
      if (!this.channelsLoaded) {
        this.hydrateChannels(snapshot.channels);
        if (this.channelsLoaded) this.channelsStale = true;
      }
      for (const _ref of Object.entries(snapshot.streams)) {
        var _ref2 = _slicedToArray(_ref, 2);
        const key = _ref2[0];
        const document = _ref2[1];
        const channelId = Number(key);
        if (!(channelId > 0)) continue;
        const stream = this.stream(channelId);
        if (stream.loadedInitial) continue;
        this.hydrateStream(stream, document, channelId);
        if (stream.loadedInitial) stream.stale = true;
      }
      for (const _ref3 of Object.entries(snapshot.pinned)) {
        var _ref4 = _slicedToArray(_ref3, 2);
        const key = _ref4[0];
        const document = _ref4[1];
        const channelId = Number(key);
        if (!(channelId > 0) || channelId in this.pinnedPreviews) continue;
        this.hydratePinned(channelId, document);
        if (channelId in this.pinnedPreviews) this.pinnedStale.add(channelId);
      }
    } catch (_unused5) {
      // Same stance as the boot payload: a snapshot that broke the chat would be
      // worse than the flash it removes.
    }
  }
  snapshotUserId() {
    var _data2;
    const id = (_data2 = app.data) == null || (_data2 = _data2.session) == null ? void 0 : _data2.userId;
    return typeof id === "number" && id > 0 ? String(id) : null;
  }

  /**
   * Writes the snapshot soon, once things settle.
   *
   * Called wherever the sidebar or a stream changes — a page landing, a message
   * arriving, a channel being read. Debounced because those come in bursts, and
   * serialising fifty messages on every one of them would cost more than the
   * snapshot saves.
   */
  scheduleSnapshot() {
    if (this.snapshotTimer !== null) return;
    this.snapshotTimer = window.setTimeout(() => {
      this.snapshotTimer = null;
      this.flushSnapshot();
    }, SNAPSHOT_DEBOUNCE_MS);
  }

  /**
   * Writes the snapshot now. Also the `pagehide` and hidden-tab hook, so a
   * message that arrived a moment before the tab closed is in the next boot.
   *
   * Only what the server has confirmed: a stale stream is the previous
   * snapshot's, and writing it back would only ever age it.
   */
  flushSnapshot() {
    if (this.snapshotTimer !== null) {
      window.clearTimeout(this.snapshotTimer);
      this.snapshotTimer = null;
    }
    const userId = this.snapshotUserId();
    if (!userId || !this.channelsLoaded || this.channelsStale) return;
    try {
      const streams = {};
      const pinned = {};
      for (const channelId of this.recentChannelIds) {
        const stream = this.streams[channelId];
        if (stream != null && stream.loadedInitial && !stream.loading && !stream.stale) {
          streams[channelId] = serialize(stream.messages.slice(-PAGE_SIZE));
        }
        if (channelId in this.pinnedPreviews && !this.pinnedStale.has(channelId)) {
          var _this$pinnedTotals$ch;
          const preview = this.pinnedPreviews[channelId];
          const document = preview ? serialize([preview]) : {
            data: []
          };
          pinned[channelId] = _objectSpread(_objectSpread({}, document), {}, {
            meta: {
              page: {
                total: (_this$pinnedTotals$ch = this.pinnedTotals[channelId]) != null ? _this$pinnedTotals$ch : 0
              }
            }
          });
        }
      }
      writeSnapshot(userId, {
        version: 1,
        savedAt: Date.now(),
        channels: serialize(this.channels),
        streams,
        pinned
      });
    } catch (_unused6) {
      // Non-fatal: the next visit loads the way it always did.
    }
  }

  /** Drops every account's snapshot; see utils/snapshot. */
  purgeSnapshots() {
    purgeSnapshots();
  }

  /**
   * Pushes a JSON:API document into the store and returns its primary models.
   *
   * `null` for a document that is absent or malformed, which the callers treat as
   * "the server did not answer this one". That is distinct from an empty `data`,
   * which is a real answer — an account with no channels, a conversation with
   * nothing pinned — and must be honoured rather than refetched.
   */
  pushDocument(document) {
    if (!document || typeof document !== "object") return null;
    const data = document.data;
    if (!Array.isArray(data)) return null;
    const pushed = app.store.pushPayload(document);
    return (Array.isArray(pushed) ? pushed : [pushed]).filter(Boolean);
  }
  hydrateChannels(document) {
    if (this.channelsLoaded) return;
    const channels = this.pushDocument(document);
    if (channels === null) return;
    this.channels = channels;
    this.channelsLoaded = true;
  }
  hydrateDrafts(document) {
    if (this.draftsLoaded) return;
    const rows = document == null ? void 0 : document.data;
    if (!Array.isArray(rows)) return;
    for (const row of rows) {
      var _row$attributes;
      const _ref5 = (_row$attributes = row == null ? void 0 : row.attributes) != null ? _row$attributes : {},
        channelId = _ref5.channelId,
        threadId = _ref5.threadId,
        content = _ref5.content;
      if (channelId && content) {
        this.drafts[this.draftKey(Number(channelId), threadId ? Number(threadId) : null)] = content;
      }
    }
    this.draftsLoaded = true;
  }

  /**
   * Seeds one message window from a preloaded page.
   *
   * The page arrives newest-first, the way the endpoint sorts it, and is reversed
   * for the same reason `fetchInto` reverses its own — the stream is held
   * oldest-first so paging upwards prepends.
   *
   * `channelId` is passed only for a channel stream: it is what the unread
   * divider is read from, and threads carry no read marker of their own.
   */
  hydrateStream(stream, document, channelId) {
    if (stream.loadedInitial || stream.loading) return;
    const page = this.pushDocument(document);
    if (page === null) return;
    if (channelId !== undefined) {
      var _channel$lastReadMess, _channel$unreadCount;
      // Frozen here for the same reason loadChannel() freezes it: the divider
      // marks where reading stopped last time, and must not follow the marker
      // down the stream as markRead() catches it up.
      const channel = this.channel(channelId);
      const lastRead = (_channel$lastReadMess = channel == null ? void 0 : channel.lastReadMessageId()) != null ? _channel$lastReadMess : 0;
      stream.dividerAfterId = lastRead > 0 && ((_channel$unreadCount = channel == null ? void 0 : channel.unreadCount()) != null ? _channel$unreadCount : 0) > 0 ? lastRead : null;
    }
    if (page.length < PAGE_SIZE) stream.hasMore = false;
    stream.messages = page.slice().reverse();
    this.sortStream(stream);
    stream.loadedInitial = true;
    stream.stale = false;
  }
  hydratePinned(channelId, document) {
    var _pinned$, _meta;
    if (channelId in this.pinnedPreviews) return;
    const pinned = this.pushDocument(document);
    if (pinned === null) return;
    this.pinnedPreviews[channelId] = (_pinned$ = pinned[0]) != null ? _pinned$ : null;

    // The preloaded document is the same one the request would have returned, so
    // it carries the same count. Read straight off it rather than through
    // `readTotal`, which expects what the store hands back.
    const total = document == null || (_meta = document.meta) == null || (_meta = _meta.page) == null ? void 0 : _meta.total;
    this.pinnedTotals[channelId] = typeof total === "number" ? total : 0;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Channels
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Fetches the sidebar's channel list, at most once per session.
   *
   * Entering the chat used to refetch the whole list every time, which is what
   * made the page flash its skeleton and rebuild itself on each visit: nothing
   * distinguished "never asked" from "asked a moment ago", so leaving the chat
   * and coming back paid for a full round trip before anything could render.
   *
   * The list does not need refetching, because nothing else lets it go stale.
   * New messages, joins, leaves and channel edits all arrive over the websocket
   * and are applied to these same objects; the poller is the backstop when the
   * socket is down. A refetch on entry would duplicate work realtime has already
   * done — which is the definition of the reload the user sees.
   *
   * `force` exists for the cases where that is not true: signing in or out
   * changes whose channels these are, and the browse page can join one behind
   * the list's back.
   *
   * `errorHandler` is for the poller, which refetches on nobody's request and
   * must not raise core's alert when the answer is that the session has ended.
   */
  async loadChannels(force, errorHandler) {
    if (force === void 0) {
      force = false;
    }
    // Join a request already in flight rather than issuing a second one.
    if (this.channelsRequest) return this.channelsRequest;
    if (this.channelsLoaded && !force) {
      // A list painted from the snapshot is served at once and refetched
      // behind it, so the sidebar never waits but never stays yesterday's.
      if (this.channelsStale) {
        this.channelsStale = false;
        this.loadChannels(true).catch(() => {});
      }
      return this.channels;
    }
    this.channelsLoading = true;
    this.channelsRequest = (async () => {
      try {
        const results = await app.store.find("chat-channels", {
          filter: {
            following: true
          },
          sort: "-lastMessageAt",
          page: {
            limit: 50
          }
        }, undefined, errorHandler ? {
          errorHandler
        } : {});
        this.channels = Array.isArray(results) ? results : [];
        this.channelsLoaded = true;
        this.channelsStale = false;
        this.scheduleSnapshot();
        return this.channels;
      } finally {
        this.channelsLoading = false;
        this.channelsRequest = null;
        m.redraw();
      }
    })();
    return this.channelsRequest;
  }

  /**
   * Drops the cached channel list so the next read refetches.
   *
   * For the changes that invalidate the whole list rather than one row — an
   * account change, or joining a channel from somewhere that does not already
   * hold the model.
   */
  /**
   * Marks a brand-new channel as loaded and empty, without asking the server.
   *
   * Only ever correct for a channel that was just created — the caller has to
   * know that, which is why it is a separate method rather than a branch inside
   * `loadChannel`. A channel inserted a moment ago in the transaction that
   * answered the request has no messages and nothing pinned, and asking for
   * either is a round trip whose answer is already known.
   *
   * Two requests saved on the path that most needs them: "Send message" on a
   * profile, where the whole point is that the conversation opens at once.
   */
  seedEmptyChannel(channelId) {
    const stream = this.stream(channelId);
    if (!stream.loadedInitial && !stream.loading) {
      stream.messages = [];
      stream.hasMore = false;
      stream.dividerAfterId = null;
      stream.loadedInitial = true;
    }
    if (!(channelId in this.pinnedPreviews)) {
      this.pinnedPreviews[channelId] = null;
    }
  }

  /**
   * Puts a channel at the top of the sidebar, if it is not already listed.
   *
   * The list is sorted by last activity and a conversation just started is the
   * most recent thing there is, so the front is where it belongs — and where the
   * server would put it on the next fetch anyway.
   */
  rememberChannel(channel) {
    if (this.channels.some(existing => existing.id() === channel.id())) return;
    this.channels.unshift(channel);
  }

  /**
   * Takes a channel off the sidebar, and off the screen if it was open.
   *
   * For a membership that ended: leaving, being removed, or a private channel
   * the reader can no longer see. The store record is left alone; a public
   * channel is still readable and the next fetch says what it may still do.
   */
  forgetChannel(channelId) {
    this.channels = this.channels.filter(channel => Number(channel.id()) !== channelId);
    if (this.activeChannelId === channelId) {
      this.closeOverlays();
      this.setActiveChannel(null);
    }
  }
  /** Returns the function that unsubscribes. */
  onMembershipChange(listener) {
    this.membershipListeners.add(listener);
    return () => {
      this.membershipListeners.delete(listener);
    };
  }
  notifyMembershipChange(channelId) {
    this.membershipListeners.forEach(listener => {
      try {
        listener(channelId);
      } catch (_unused7) {
        // One listener failing must not stop the others from being told.
      }
    });
  }

  /**
   * Warms everything opening a channel would ask for, without waiting for it.
   *
   * The boot payload only helps a page that was loaded from the server; arriving
   * at the chat from a link, or switching channels once inside it, is a client-
   * side navigation with nothing preloaded. Called from hover and focus, it turns
   * the round trip into something that happens while the pointer is still moving,
   * so the click lands on a conversation that is already there.
   *
   * Both calls are idempotent and self-guarding — a channel already loaded costs
   * nothing — so this is safe to fire as often as the pointer moves.
   */
  prefetchChannel(channelId) {
    this.loadChannel(channelId).catch(() => {});
    this.loadPinnedPreview(channelId).catch(() => {});
  }
  invalidateChannels() {
    this.channelsLoaded = false;
  }
  channel(id) {
    var _ref6;
    if (id === null) return null;
    return (_ref6 = app.store.getById("chat-channels", String(id))) != null ? _ref6 : null;
  }

  /** Category channels, for the sidebar's "Channels" section. */
  categoryChannels() {
    return this.channels.filter(c => c.isCategory());
  }

  /** Direct and group channels, for the "Direct Messages" section. */
  directChannels() {
    return this.channels.filter(c => c.isDirect());
  }

  /**
   * Channels with unread messages, and how many mentions are outstanding.
   *
   * Prefers the loaded channel list, because that reflects realtime pushes the
   * moment they land — `bumpChannel` increments it directly.
   *
   * Falls back to the counters serialised onto the session user, which are present
   * in the page payload from the first paint. Without the fallback the collapsed
   * drawer showed nothing until `loadChannels()` resolved, and nothing at all if
   * the drawer was opened without ever loading the list — which read as "the dot
   * does not work".
   */
  unreadSummary() {
    var _user$attribute, _user$attribute2, _user$attribute3;
    if (this.channelsLoaded && this.channels.length > 0) {
      let channels = 0;
      let messages = 0;
      let mentions = 0;
      for (const channel of this.channels) {
        var _channel$unreadMentio;
        // Muted channels carry no badge, so they must not feed the header count
        // either — otherwise the number says there is something to read in a
        // channel the user deliberately silenced.
        if (channel.isMuted()) continue;
        if (channel.hasUnread()) {
          var _channel$unreadCount2;
          channels++;
          messages += (_channel$unreadCount2 = channel.unreadCount()) != null ? _channel$unreadCount2 : 0;
        }
        if (channel.hasUnreadMentions()) mentions += (_channel$unreadMentio = channel.unreadMentionsCount()) != null ? _channel$unreadMentio : 0;
      }
      return {
        channels,
        messages,
        mentions
      };
    }

    // Before the channel list is loaded — which is the usual state when the chat
    // has not been opened yet — the serialised counters are all there is. Realtime
    // keeps them moving; see bumpUnreadCounters().
    const user = app.session.user;
    return {
      channels: Number((_user$attribute = user == null ? void 0 : user.attribute("chatUnreadChannelsCount")) != null ? _user$attribute : 0),
      messages: Number((_user$attribute2 = user == null ? void 0 : user.attribute("chatUnreadMessagesCount")) != null ? _user$attribute2 : 0),
      mentions: Number((_user$attribute3 = user == null ? void 0 : user.attribute("chatUnreadMentionsCount")) != null ? _user$attribute3 : 0)
    };
  }

  /**
   * Moves the actor's own serialised counters.
   *
   * They are a snapshot taken when the page was rendered, and the header badge and
   * the nav dot fall back to them whenever the channel list has not been loaded —
   * which is exactly the situation the badge exists for. Without this the dot only
   * ever appeared after a reload.
   */
  bumpUnreadCounters(messages, mentions, newChannel) {
    const user = app.session.user;
    if (!user) return;
    const at = key => {
      var _user$attribute4;
      return Number((_user$attribute4 = user.attribute(key)) != null ? _user$attribute4 : 0);
    };
    user.pushAttributes({
      chatUnreadMessagesCount: Math.max(0, at("chatUnreadMessagesCount") + messages),
      chatUnreadMentionsCount: Math.max(0, at("chatUnreadMentionsCount") + mentions),
      chatUnreadChannelsCount: Math.max(0, at("chatUnreadChannelsCount") + (newChannel ? 1 : 0))
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Message stream
  // ───────────────────────────────────────────────────────────────────────────

  stream(channelId) {
    if (!this.streams[channelId]) {
      this.streams[channelId] = {
        messages: [],
        hasMore: true,
        loading: false,
        loadedInitial: false,
        dividerAfterId: null,
        stale: false
      };
    }
    return this.streams[channelId];
  }

  /**
   * Loads the newest page and freezes the unread divider at the read marker as
   * it stood on entry.
   */
  async loadChannel(channelId) {
    var _channel$lastReadMess2, _channel$unreadCount3;
    const stream = this.stream(channelId);

    // Painted from the snapshot: it is on screen already, so nothing is awaited.
    // The server's page replaces it as soon as it lands.
    if (stream.stale && stream.loadedInitial && !stream.loading) {
      stream.stale = false;
      this.revalidateStream(channelId).catch(() => {});
      return;
    }
    if (stream.loadedInitial || stream.loading) return;
    const channel = this.channel(channelId);
    const lastRead = (_channel$lastReadMess2 = channel == null ? void 0 : channel.lastReadMessageId()) != null ? _channel$lastReadMess2 : 0;

    // Only show a divider when there is actually something unread below it.
    stream.dividerAfterId = lastRead > 0 && ((_channel$unreadCount3 = channel == null ? void 0 : channel.unreadCount()) != null ? _channel$unreadCount3 : 0) > 0 ? lastRead : null;
    await this.fetchPage(channelId);
    stream.loadedInitial = true;
  }

  /**
   * Fetches one page older than what is loaded. The API sorts newest-first, so a
   * page is reversed before being prepended.
   */
  async fetchPage(channelId) {
    await this.fetchInto(this.stream(channelId), {
      channel: channelId
    });
  }

  /**
   * Pages one stream backwards from a base filter.
   *
   * Shared by the channel and thread streams: both are windows over the same
   * endpoint differing only in which filter selects them, and paging that drifts
   * between the two would show a thread a different history than the channel.
   */
  async fetchInto(stream, filter) {
    if (stream.loading || !stream.hasMore) return;
    stream.loading = true;
    try {
      const oldest = stream.messages[0];
      const results = await app.store.find("chat-messages", {
        filter: _objectSpread(_objectSpread({}, filter), oldest ? {
          lessThan: Number(oldest.id())
        } : {}),
        sort: "-id",
        page: {
          limit: PAGE_SIZE
        }
      });
      const page = (Array.isArray(results) ? results : []).slice().reverse();
      if (page.length < PAGE_SIZE) stream.hasMore = false;

      // Merge rather than concat: a realtime push may already have inserted one
      // of these, and a duplicate row is worse than a missing one.
      const known = new Set(stream.messages.map(msg => msg.id()));
      const fresh = page.filter(msg => !known.has(msg.id()));
      stream.messages = [...fresh, ...stream.messages];
      this.sortStream(stream);
      this.scheduleSnapshot();
    } finally {
      stream.loading = false;
      m.redraw();
    }
  }

  /**
   * Replaces a snapshot-seeded window with the server's newest page.
   *
   * One request, the same one a cold open makes — but not awaited, because the
   * conversation is already drawn. Within the range the page covers the server
   * is the authority: rows it no longer returns were deleted while this browser
   * was away and go; rows it returns land edited, reacted and re-pinned as they
   * now are. Rows older than the page were not asked about and are kept — the
   * reader scrolled to them last time and can still see them.
   *
   * On failure the snapshot stays on screen unrevalidated; the poller's
   * `greaterThan` and `updatedSince` cursors catch it up on the next tick.
   */
  async revalidateStream(channelId) {
    const stream = this.stream(channelId);
    if (stream.loading) return;
    stream.loading = true;
    try {
      const results = await app.store.find("chat-messages", {
        filter: {
          channel: channelId
        },
        sort: "-id",
        page: {
          limit: PAGE_SIZE
        }
      });
      const fresh = (Array.isArray(results) ? results : []).slice().reverse();
      const complete = fresh.length < PAGE_SIZE;
      const oldest = fresh[0] ? Number(fresh[0].id()) : 0;
      const kept = complete ? [] : stream.messages.filter(msg => Number(msg.id()) < oldest);
      stream.messages = [...kept, ...fresh];
      this.sortStream(stream);
      if (complete) stream.hasMore = false;
      this.scheduleSnapshot();
    } finally {
      stream.loading = false;
      m.redraw();
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Threads
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * A thread's own message window, keyed by thread id.
   *
   * Kept apart from `streams` rather than filtered out of the channel window: the
   * channel shows a thread only as an indicator under its root message, so the
   * replies are never in that window to begin with.
   */
  threadStream(threadId) {
    if (!this.threadStreams[threadId]) {
      this.threadStreams[threadId] = {
        messages: [],
        hasMore: true,
        loading: false,
        loadedInitial: false,
        // Threads carry no read marker of their own, so no divider.
        dividerAfterId: null,
        stale: false
      };
    }
    return this.threadStreams[threadId];
  }
  async loadThread(threadId) {
    const stream = this.threadStream(threadId);
    if (stream.loadedInitial || stream.loading) return;
    await this.fetchThreadPage(threadId);
    stream.loadedInitial = true;
  }
  async fetchThreadPage(threadId) {
    await this.fetchInto(this.threadStream(threadId), {
      thread: threadId
    });
  }

  /**
   * Loads the thread record itself, for the panel's title and reply count.
   */
  async findThread(threadId) {
    const known = app.store.getById("chat-threads", String(threadId));
    if (known) return known;
    try {
      var _ref7;
      return (_ref7 = await app.store.find("chat-threads", String(threadId))) != null ? _ref7 : null;
    } catch (_unused8) {
      return null;
    }
  }
  closeThread() {
    this.activeThreadId = null;
  }

  /**
   * Leaves whatever is covering the conversation in the drawer.
   *
   * Called when the drawer changes channel: a search or a pin list belongs to
   * the channel it was opened from, and carrying it across to the next one shows
   * one channel's results over another's conversation.
   */
  closeOverlays() {
    this.showPinned = false;
    this.showSearch = false;
    this.showThreads = false;
    this.activeThreadId = null;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Pinned
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Fetches the newest pin that sits above the loaded window, once per channel.
   *
   * `latestPinned()` already answers from the loaded window whenever it can, so
   * this only exists for a pin far enough back that the stream has not reached
   * it — which is a fixed fact about the channel's history, not something that
   * needs re-asking on every visit. It was being refetched on every channel
   * open, and each one is a full message read: the resource resolves nine
   * capability policies and four relation-backed summaries to return one row.
   *
   * `channelId in this.pinnedPreviews` rather than a truthiness check, because
   * `null` here means "asked, nothing pinned" and must not be re-asked; only
   * `undefined` means "never asked".
   *
   * Realtime keeps the answer honest: a pin or unpin on a message this client
   * already holds updates that model in place, and a pin on one it does not
   * drops this cache — see `onMessageChanged`.
   */
  async loadPinnedPreview(channelId, force) {
    if (force === void 0) {
      force = false;
    }
    if (!force && channelId in this.pinnedPreviews) {
      // From the snapshot: shown already, re-asked behind it.
      if (this.pinnedStale.delete(channelId)) {
        this.loadPinnedPreview(channelId, true).catch(() => {});
      }
      return;
    }
    try {
      var _ref8, _readTotal;
      const results = await app.store.find("chat-messages", {
        filter: {
          channel: channelId,
          pinned: true,
          includeThreadReplies: true
        },
        sort: "-pinnedAt",
        page: {
          limit: 1
        }
      });
      this.pinnedPreviews[channelId] = (_ref8 = Array.isArray(results) ? results[0] : null) != null ? _ref8 : null;

      // How many there are in total, which is what decides whether the bar
      // offers a way into the full list. It comes back in this same response —
      // json-api-server puts a count beside every paginated collection — so
      // knowing it costs nothing beyond the request already being made.
      this.pinnedTotals[channelId] = (_readTotal = readTotal(results)) != null ? _readTotal : 0;
      this.scheduleSnapshot();
    } catch (_unused9) {
      // A missing bar is not worth an error; the pin itself still shows on the row.
      this.pinnedPreviews[channelId] = null;
      this.pinnedTotals[channelId] = 0;
    } finally {
      m.redraw();
    }
  }

  /**
   * Drops a channel's cached pin, so the next open asks the server again.
   *
   * For the one case realtime cannot reconcile on its own: a message pinned
   * above the loaded window, which this client is not holding and therefore
   * cannot update in place.
   */
  invalidatePinnedPreview(channelId) {
    delete this.pinnedPreviews[channelId];
    delete this.pinnedTotals[channelId];
  }

  /**
   * How many pinned messages the channel has.
   *
   * The larger of what the server last counted and what is pinned in the loaded
   * window, because neither sees everything: the count cannot know about a pin
   * made since it was taken, and the window cannot know about one above it. Both
   * being wrong in only one direction is what makes the maximum the honest
   * answer rather than a guess.
   *
   * Used to decide whether the pinned bar is worth a second control. With one
   * pin the bar already shows it and clicking jumps to it, so a panel listing
   * that same message would be a click to see what is on screen.
   */
  pinnedCount(channelId) {
    var _this$pinnedTotals$ch2;
    const known = new Set();
    const preview = this.pinnedPreviews[channelId];
    if (preview != null && preview.isPinned() && preview.id()) known.add(String(preview.id()));
    for (const message of (_this$streams$channel = (_this$streams$channel2 = this.streams[channelId]) == null ? void 0 : _this$streams$channel2.messages) != null ? _this$streams$channel : []) {
      var _this$streams$channel, _this$streams$channel2;
      if (message.isPinned() && !message.isDeleted() && message.id()) {
        known.add(String(message.id()));
      }
    }
    return Math.max((_this$pinnedTotals$ch2 = this.pinnedTotals[channelId]) != null ? _this$pinnedTotals$ch2 : 0, known.size);
  }

  /**
   * The message the pinned bar should show.
   *
   * Computed over the loaded window as well as the fetched preview, and the newest
   * `pinnedAt` wins. That way pinning something in view updates the bar at once,
   * with no refetch, and unpinning the message the bar was showing drops it.
   */
  latestPinned(channelId) {
    const candidates = [];
    const preview = this.pinnedPreviews[channelId];
    if (preview && preview.isPinned()) candidates.push(preview);
    for (const message of (_this$streams$channel3 = (_this$streams$channel4 = this.streams[channelId]) == null ? void 0 : _this$streams$channel4.messages) != null ? _this$streams$channel3 : []) {
      var _this$streams$channel3, _this$streams$channel4;
      if (message.isPinned() && !message.isDeleted()) candidates.push(message);
    }
    if (candidates.length === 0) return null;
    return candidates.reduce((newest, message) => {
      var _message$pinnedAt$get, _message$pinnedAt, _newest$pinnedAt$getT, _newest$pinnedAt;
      return ((_message$pinnedAt$get = (_message$pinnedAt = message.pinnedAt()) == null ? void 0 : _message$pinnedAt.getTime()) != null ? _message$pinnedAt$get : 0) > ((_newest$pinnedAt$getT = (_newest$pinnedAt = newest.pinnedAt()) == null ? void 0 : _newest$pinnedAt.getTime()) != null ? _newest$pinnedAt$getT : 0) ? message : newest;
    });
  }

  /** The pinned panel, the search pane and the thread panel share one slot. */
  togglePinned() {
    this.showPinned = !this.showPinned;
    if (this.showPinned) {
      this.activeThreadId = null;
      this.showSearch = false;
      this.showThreads = false;
    }
  }

  /** Search, in the drawer's overlay slot. See `showSearch`. */
  toggleSearch() {
    this.showSearch = !this.showSearch;
    if (this.showSearch) {
      this.activeThreadId = null;
      this.showPinned = false;
      this.showThreads = false;
    }
  }
  toggleThreads() {
    this.showThreads = !this.showThreads;
    if (this.showThreads) {
      this.activeThreadId = null;
      this.showPinned = false;
      this.showSearch = false;
    }
  }

  /** Chronological, by id. Ids are monotonic per channel. */
  sortStream(stream) {
    stream.messages.sort((a, b) => Number(a.id()) - Number(b.id()));
  }

  /**
   * Inserts or replaces a message in its channel's stream. Used by both the send
   * path and realtime, so ordering and de-duplication live in one place.
   */
  upsertMessage(message) {
    const channelId = message.channelId();
    if (!channelId) return;
    const threadId = message.threadId();

    // A thread reply belongs to the open thread panel. Routed here rather than at
    // each call site so realtime and the send path cannot disagree about it.
    if (threadId) {
      this.insertInto(this.threadStreams[threadId], message, true);
    }

    // A thread *reply* must not be appended to the channel window: the API's
    // channel filter keeps thread roots and drops their replies, so appending one
    // would inline in realtime what a reload shows only as an indicator. Updating
    // a row already in the window stays safe — that is the root gaining a thread.
    //
    // Nothing is created for a channel that was never opened; its badge is enough
    // until the user looks at it.
    this.insertInto(this.streams[channelId], message, !threadId || this.isThreadRoot(message));
    this.scheduleSnapshot();
  }
  isThreadRoot(message) {
    // `false` here means the relationship is not loaded, and a message with no
    // thread on it cannot be a root. That used to be the answer for every
    // realtime push; the thread broadcast now links the root as it lands, so
    // this is only reached for replies and for roots nobody has scrolled to.
    const thread = message.thread();
    if (!thread) return false;
    return thread.originalMessageId() === Number(message.id());
  }
  insertInto(stream, message, appendIfNew) {
    if (!stream) return;
    const index = stream.messages.findIndex(msg => msg.id() === message.id());
    if (index >= 0) {
      stream.messages[index] = message;
      return;
    }
    if (!appendIfNew) return;
    stream.messages.push(message);
    this.sortStream(stream);
  }
  removeMessage(channelId, messageId) {
    const stream = this.streams[channelId];
    if (!stream) return;
    stream.messages = stream.messages.filter(msg => Number(msg.id()) !== messageId);
    this.scheduleSnapshot();
  }

  /**
   * The same, for the panel a thread renders in.
   *
   * Separate because thread streams live in their own map: passing a thread id
   * to `removeMessage` would look it up among the channels, and on a forum where
   * a channel happens to carry that id it would sweep the wrong conversation.
   */
  removeThreadMessage(threadId, messageId) {
    const stream = this.threadStreams[threadId];
    if (!stream) return;
    stream.messages = stream.messages.filter(msg => Number(msg.id()) !== messageId);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Sending
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Sends a message. Returns the saved model.
   *
   * The composer is cleared before the request resolves so typing can continue
   * immediately; on failure the content is handed back so nothing is lost.
   */
  async send(channelId, content, options) {
    if (options === void 0) {
      options = {};
    }
    const trimmed = content.trim();
    const uploadIds = this.pendingUploads.map(u => Number(u.id()));
    if (!trimmed && uploadIds.length === 0) return null;
    const token = "pending-".concat(++this.pendingSeq);
    this.pending.set(token, {
      channelId,
      token
    });
    try {
      var _options$threadId, _options$replyToId, _options$createThread, _options$threadId2, _options$threadId3;
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages"),
        body: {
          data: {
            type: "chat-messages",
            attributes: {
              channelId,
              content: trimmed,
              threadId: (_options$threadId = options.threadId) != null ? _options$threadId : null,
              replyToId: (_options$replyToId = options.replyToId) != null ? _options$replyToId : null,
              createThread: (_options$createThread = options.createThread) != null ? _options$createThread : false,
              uploadIds
            }
          }
        }
      });
      const message = app.store.pushPayload(payload);
      this.upsertMessage(message);
      this.pendingUploads = [];
      this.clearContext(channelId, (_options$threadId2 = options.threadId) != null ? _options$threadId2 : null);

      // The sender is implicitly caught up.
      const channel = this.channel(channelId);
      if (channel) {
        channel.pushAttributes({
          unreadCount: 0,
          lastReadMessageId: Number(message.id())
        });
      }
      this.clearDraft(channelId, (_options$threadId3 = options.threadId) != null ? _options$threadId3 : null);
      return message;
    } finally {
      this.pending.delete(token);
      m.redraw();
    }
  }
  hasPendingSends(channelId) {
    for (const entry of this.pending.values()) {
      if (entry.channelId === channelId) return true;
    }
    return false;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Read state
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Marks a channel read up to its newest loaded message.
   *
   * Fire-and-forget: a failed read receipt is not worth surfacing, and the next
   * call will carry a newer marker anyway.
   *
   * Deliberately *not* gated on `hasUnread()`. A message arriving in the channel
   * you are looking at is marked read without ever being badged — realtime's
   * `bumpChannel` calls straight through here for the active channel — so the
   * badge is already zero and that guard turned every one of those calls into a
   * no-op. The marker then stopped at whatever was newest when the channel was
   * opened. Nothing showed it while the marker only drove a badge that was
   * correct anyway; read receipts read the marker directly, which is how it
   * surfaced as "seen" never reaching the newest message.
   *
   * The comparison below is the honest guard: it stops exactly when the marker
   * is already at or past the newest loaded message, which is also the condition
   * the server uses to decide whether the move is worth broadcasting.
   */
  markRead(channelId) {
    var _channel$lastReadMess3;
    const channel = this.channel(channelId);
    if (!channel) return;
    const stream = this.streams[channelId];
    const newest = stream == null ? void 0 : stream.messages[stream.messages.length - 1];
    const upTo = newest ? Number(newest.id()) : channel.lastMessageId();
    if (!upTo) return;
    if (((_channel$lastReadMess3 = channel.lastReadMessageId()) != null ? _channel$lastReadMess3 : 0) >= upTo) return;
    channel.pushAttributes({
      unreadCount: 0,
      unreadMentionsCount: 0,
      lastReadMessageId: upTo
    });
    this.scheduleSnapshot();
    app.request({
      method: "POST",
      url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(channelId, "/read"),
      body: {
        data: {
          attributes: {
            lastReadMessageId: upTo
          }
        }
      }
    }).catch(() => {
      // Swallowed deliberately — see above.
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Drafts
  // ───────────────────────────────────────────────────────────────────────────

  draftKey(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    return threadId ? "".concat(channelId, ":").concat(threadId) : String(channelId);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Composer context (reply / edit), per scope
  // ───────────────────────────────────────────────────────────────────────────

  replyingTo(channelId, threadId) {
    var _this$replyTargets$th;
    if (threadId === void 0) {
      threadId = null;
    }
    return (_this$replyTargets$th = this.replyTargets[this.draftKey(channelId, threadId)]) != null ? _this$replyTargets$th : null;
  }
  editing(channelId, threadId) {
    var _this$editTargets$thi;
    if (threadId === void 0) {
      threadId = null;
    }
    return (_this$editTargets$thi = this.editTargets[this.draftKey(channelId, threadId)]) != null ? _this$editTargets$thi : null;
  }

  /** Whether the staged reply was started as a branch rather than a reply. */
  branchingFrom(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    return this.branchTargets[this.draftKey(channelId, threadId)] === true;
  }

  /**
   * Replying and editing are mutually exclusive within a scope.
   *
   * `branch` says the reply was staged by "reply in thread" rather than by
   * "reply". Only the composer reads it, and only to decide whether the send
   * should open a thread — see ChatComposer.submit().
   */
  setReplyingTo(channelId, message, threadId, branch) {
    if (threadId === void 0) {
      threadId = null;
    }
    if (branch === void 0) {
      branch = false;
    }
    const key = this.draftKey(channelId, threadId);
    delete this.editTargets[key];
    delete this.branchTargets[key];
    if (message) {
      this.replyTargets[key] = message;
      if (branch) this.branchTargets[key] = true;
    } else {
      delete this.replyTargets[key];
    }
  }
  setEditing(channelId, message, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    const key = this.draftKey(channelId, threadId);
    delete this.replyTargets[key];
    delete this.branchTargets[key];
    if (message) {
      this.editTargets[key] = message;
    } else {
      delete this.editTargets[key];
    }
  }
  clearContext(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    const key = this.draftKey(channelId, threadId);
    delete this.replyTargets[key];
    delete this.editTargets[key];
    delete this.branchTargets[key];
  }
  draft(channelId, threadId) {
    var _this$drafts$this$dra;
    if (threadId === void 0) {
      threadId = null;
    }
    return (_this$drafts$this$dra = this.drafts[this.draftKey(channelId, threadId)]) != null ? _this$drafts$this$dra : "";
  }
  setDraft(channelId, content, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    const key = this.draftKey(channelId, threadId);
    if (content.trim() === "") {
      delete this.drafts[key];
    } else {
      this.drafts[key] = content;
    }
    this.persistDraft(channelId, threadId, content);
  }
  clearDraft(channelId, threadId) {
    if (threadId === void 0) {
      threadId = null;
    }
    delete this.drafts[this.draftKey(channelId, threadId)];
    this.persistDraft(channelId, threadId, "");
  }
  /**
   * Debounced so a keystroke does not become a request. Drafts are server-side so
   * they follow the user across devices, but they are not worth a write per
   * character.
   */
  persistDraft(channelId, threadId, content) {
    if (this.draftTimer !== null) window.clearTimeout(this.draftTimer);
    this.draftTimer = window.setTimeout(() => {
      app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/drafts"),
        body: {
          data: {
            attributes: {
              channelId,
              threadId,
              content
            }
          }
        }
      }).catch(() => {});
    }, 1200);
  }

  /**
   * Restores saved composer drafts, at most once per session.
   *
   * Cached for the same reason the channel list is, and one more: this is the
   * only reader of a value the composer is continuously writing. Refetching on
   * every visit to the chat raced a save that had not yet landed, so a draft
   * typed just before navigating away could come back as its previous revision.
   */
  async loadDrafts() {
    // Drafts belong to an account, and the endpoint is authenticated. Guarded here
    // rather than at each of the three call sites: a guest asking for them gets a
    // 401 whose rejection surfaces as an error alert over a chat that is otherwise
    // working, and a fourth call site would reintroduce it.
    if (!app.session.user) return;
    if (this.draftsRequest) return this.draftsRequest;
    if (this.draftsLoaded) return;
    this.draftsRequest = this.fetchDrafts().finally(() => {
      this.draftsRequest = null;
    });
    return this.draftsRequest;
  }
  async fetchDrafts() {
    try {
      const payload = await app.request({
        method: "GET",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/drafts")
      });
      for (const row of (_payload$data = payload.data) != null ? _payload$data : []) {
        var _payload$data, _row$attributes2;
        const _ref9 = (_row$attributes2 = row.attributes) != null ? _row$attributes2 : {},
          channelId = _ref9.channelId,
          threadId = _ref9.threadId,
          content = _ref9.content;
        if (channelId && content) {
          this.drafts[this.draftKey(Number(channelId), threadId ? Number(threadId) : null)] = content;
        }
      }
      this.draftsLoaded = true;
    } catch (_unused0) {
      // Drafts are a convenience; failing to restore them must not block the UI.
      // Deliberately not marked loaded, so the next visit retries.
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Typing
  // ───────────────────────────────────────────────────────────────────────────

  /** Live typists in a channel, excluding entries that have expired. */
  typistsIn(channelId) {
    const entries = this.typing[channelId];
    if (!entries) return [];
    const now = Date.now();
    const names = [];
    for (const _ref0 of Object.entries(entries)) {
      var _ref1 = _slicedToArray(_ref0, 2);
      const userId = _ref1[0];
      const entry = _ref1[1];
      if (entry.expiresAt <= now) {
        window.clearTimeout(entry.timer);
        delete entries[Number(userId)];
        continue;
      }
      names.push(entry.username);
    }
    return names;
  }
  noteTyping(channelId, userId, username, typing, expiresIn) {
    if (expiresIn === void 0) {
      expiresIn = 6;
    }
    if (!this.typing[channelId]) this.typing[channelId] = {};

    // Whatever happens next replaces this entry, so its pending redraw is stale.
    // Left running, a stopped typist's old timer would fire against a fresh
    // entry — and a stream of keystrokes would pile up one timer per event.
    const previous = this.typing[channelId][userId];
    if (previous) window.clearTimeout(previous.timer);
    if (!typing) {
      delete this.typing[channelId][userId];
      return;
    }

    // A shade past the deadline, so the redraw reads an entry that has already
    // expired rather than one expiring on the same millisecond.
    const timer = window.setTimeout(() => m.redraw(), expiresIn * 1000 + 100);
    this.typing[channelId][userId] = {
      username,
      expiresAt: Date.now() + expiresIn * 1000,
      timer
    };
  }

  /**
   * Drops a typist outright — they have just said what they were typing.
   *
   * Waiting for the entry to expire leaves "X is typing…" sitting under the very
   * message X sent, for as long as the window that was still open when it
   * arrived.
   */
  clearTyping(channelId, userId) {
    this.noteTyping(channelId, userId, "", false);
  }
  /**
   * Announces typing, throttled to at most once every 3s. Without the throttle
   * this would be one request per keystroke.
   */
  announceTyping(channelId) {
    const now = Date.now();
    if (now - this.typingSentAt < 3000) return;
    this.typingSentAt = now;
    app.request({
      method: "POST",
      url: "".concat(app.forum.attribute("apiUrl"), "/chat/typing"),
      body: {
        data: {
          attributes: {
            channelId,
            typing: true
          }
        }
      }
    }).catch(() => {});
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Selection
  // ───────────────────────────────────────────────────────────────────────────

  toggleSelecting(on) {
    this.selecting = on != null ? on : !this.selecting;
    if (!this.selecting) this.selected.clear();
  }
  toggleSelected(messageId) {
    if (this.selected.has(messageId)) {
      this.selected.delete(messageId);
    } else {
      this.selected.add(messageId);
    }
  }

  /**
   * Renders the current selection as a transcript. Server-side so quoting rules
   * match archiving exactly.
   */
  async transcript(format) {
    var _payload$data$attribu, _payload$data2;
    if (format === void 0) {
      format = "markup";
    }
    if (this.selected.size === 0) return "";
    const payload = await app.request({
      method: "POST",
      url: "".concat(app.forum.attribute("apiUrl"), "/chat/transcript"),
      body: {
        data: {
          attributes: {
            messageIds: Array.from(this.selected),
            format
          }
        }
      }
    });
    return (_payload$data$attribu = (_payload$data2 = payload.data) == null || (_payload$data2 = _payload$data2.attributes) == null ? void 0 : _payload$data2.content) != null ? _payload$data$attribu : "";
  }
}
flarum.reg.add('ramon-chat', 'forum/state/ChatState', ChatState);